import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Container,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Chip,
  Collapse,
} from '@mui/material';
import { FaArrowLeft, FaPlus, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { getAllCourseDetails, getAllSubjectDetails, getCoursePptStructure } from '../Servies/AdmiServices';

const PRIMARY = '#2065D1';
const PRIMARY_DARK = '#103996';

export default function PptViewer() {
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  const [courseId, setCourseId] = useState('');
  const [subjectLevelId, setSubjectLevelId] = useState('');

  const [loading, setLoading] = useState(false);
  const [structure, setStructure] = useState(null);
  const [openChapters, setOpenChapters] = useState({});

  useEffect(() => {
    getAllCourseDetails()
      .then((res) => setCourses(res?.data || []))
      .catch(() => {});
  }, []);

  const handleCourseChange = (value) => {
    setCourseId(value);
    setSubjectLevelId('');
    setSubjects([]);
    setStructure(null);
    if (!value) return;
    setSubjectsLoading(true);
    getAllSubjectDetails(value)
      .then((res) => setSubjects(res?.data || []))
      .catch(() => {})
      .finally(() => setSubjectsLoading(false));
  };

  const handleLoadStructure = async () => {
    if (!courseId) {
      toast.error('Please select a course.');
      return;
    }
    setLoading(true);
    setStructure(null);
    try {
      const res = await getCoursePptStructure(courseId, subjectLevelId || undefined);
      setStructure(res?.data || null);
      setOpenChapters({});
    } catch (err) {
      toast.error('Failed to load PPT structure for this course.');
    } finally {
      setLoading(false);
    }
  };

  const toggleChapter = (chapterId) => {
    setOpenChapters((prev) => ({ ...prev, [chapterId]: !prev[chapterId] }));
  };

  const handleAddChapter = () => {
    navigate('/dashboard/AddChapterContent', { state: { courseId } });
  };

  return (
    <>
      <ToastContainer />
      <Helmet>
        <title> Dashboard: PPT Viewer </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" spacing={2} mb={4}>
          <Tooltip title="Back">
            <IconButton onClick={() => navigate(-1)}>
              <FaArrowLeft />
            </IconButton>
          </Tooltip>
          <Typography variant="h4">PPT Viewer</Typography>
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2} alignItems="center" mb={4}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="ppt-course-label">Course</InputLabel>
            <Select
              labelId="ppt-course-label"
              label="Course"
              value={courseId}
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              {courses.map((course) => (
                <MenuItem key={course.courseId} value={course.courseId}>
                  {course.courseDisplayName || course.courseId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 260 }} disabled={!courseId || subjectsLoading}>
            <InputLabel id="ppt-subject-label">Subject (optional)</InputLabel>
            <Select
              labelId="ppt-subject-label"
              label="Subject (optional)"
              value={subjectLevelId}
              onChange={(e) => setSubjectLevelId(e.target.value)}
            >
              <MenuItem value="">All Subjects</MenuItem>
              {subjects.map((subject) => (
                <MenuItem key={subject.levelId} value={subject.levelId}>
                  {subject.levelDisplayName || subject.levelId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" onClick={handleLoadStructure} disabled={!courseId || loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Load'}
          </Button>

          <Button variant="outlined" startIcon={<FaPlus />} onClick={handleAddChapter} disabled={!courseId}>
            Add Chapter
          </Button>
        </Stack>

        {structure && (
          <Box>
            <Typography variant="h5" gutterBottom>
              {structure.courseName || structure.courseId}
            </Typography>

            {(!structure.chapters || structure.chapters.length === 0) && (
              <Typography variant="body2" color="text.secondary">
                No chapters found for this course{subjectLevelId ? ' / subject' : ''} yet.
              </Typography>
            )}

            {structure.chapters?.map((chapter) => {
              const isOpen = !!openChapters[chapter.chapterId];
              const hasPpts = chapter.ppts?.length > 0;
              return (
                <Box key={chapter.chapterId} sx={{ mb: 1 }}>
                  <Box
                    onClick={() => toggleChapter(chapter.chapterId)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 1,
                      px: 1,
                      cursor: 'pointer',
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {isOpen ? (
                      <FaChevronDown size={12} color={PRIMARY} />
                    ) : (
                      <FaChevronRight size={12} color={PRIMARY} />
                    )}
                    <Typography variant="subtitle1" fontWeight={700}>
                      {chapter.chapterName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (Seq {chapter.sequenceNo} · {chapter.ppts?.length || 0} PPT{chapter.ppts?.length === 1 ? '' : 's'})
                    </Typography>
                  </Box>

                  <Collapse in={isOpen} unmountOnExit>
                    <Box sx={{ pl: 4, pb: 1 }}>
                      {hasPpts ? (
                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ my: 1 }}>
                          {chapter.ppts.map((ppt) => (
                            <Chip
                              key={ppt.contentId}
                              label={`${ppt.sequenceNo}. ${ppt.contentName}`}
                              sx={{
                                bgcolor: PRIMARY,
                                color: '#fff',
                                fontWeight: 500,
                                '&:hover': { bgcolor: PRIMARY_DARK },
                              }}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No PPTs in this chapter yet.
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
      </Container>
    </>
  );
}
